import {
  TableCell,
  Table,
  TableRow,
  TableContainer,
  TableHead,
  Box,
  Typography,
  TableBody,
} from "@mui/material";
import { addDays, format, isToday, startOfWeek } from "date-fns";
import { AppointmentData } from "hooks/useAppointments";
import { themeColors } from "theme/colorTokens";

export interface AppointmentWeekViewProps {
  currentDate: Date;
  timeSlots: Date[];
  getAppointmentStyle: (appointment: AppointmentData) => React.CSSProperties;
  getAppointmentsForDay: (day: Date) => AppointmentData[];
  handleEditAppointment: (appointment: AppointmentData) => void;
}
const AppointmentWeekView = ({
  currentDate,
  timeSlots,
  getAppointmentStyle,
  getAppointmentsForDay,
  handleEditAppointment,
}: AppointmentWeekViewProps) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const handleAppointmentShowed = (day: Date, time: Date) => {
    return (
      <TableCell
        key={day.toISOString()}
        sx={{
          padding: 0,
          position: "relative",
          height: "60px",
          border: "none",
        }}
      >
        <Box
          sx={{
            borderBottom: `1px solid ${themeColors.border.light}`,
            padding: 0,
            margin: 0,
            zIndex: -1,
          }}
        ></Box>
        <Box
          sx={{
            border: "none",
            padding: 0,
            margin: 0,
            zIndex: -1,
          }}
        ></Box>
        {getAppointmentsForDay(day)
          .filter((appointment) => {
            if (!appointment.startTime) return false;
            const appointmentHour = appointment.startTime.getHours();
            const slotHour = time.getHours();

            return appointmentHour === slotHour;
          })
          .map((appointment) => (
            <Box
              key={appointment.id}
              className="absolute rounded cursor-pointer"
              style={{
                ...getAppointmentStyle(appointment),
                left: "1px",
                right: "1px",
              }}
              onClick={() => handleEditAppointment(appointment)}
              sx={{
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <Box sx={{ padding: "6px 8px" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: themeColors.text.bold,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    lineHeight: 1.2,
                    textAlign: "center",
                    display: "block",
                  }}
                >
                  {appointment.patientName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: themeColors.text.secondary,
                    fontWeight: 400,
                    fontSize: "0.7rem",
                    lineHeight: 1.2,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {appointment.startTime
                    ? format(appointment.startTime, "h:mm a")
                    : "--"}{" "}
                  -{" "}
                  {appointment.endTime
                    ? format(appointment.endTime, "h:mm a")
                    : "--"}
                </Typography>
              </Box>
            </Box>
          ))}
      </TableCell>
    );
  };

  return (
    <TableContainer
      sx={{
        height: "100%",
        "& .MuiTable-root": {
          tableLayout: "fixed",
        },
        display: "contents",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: "80px",
                backgroundColor: themeColors.background.paper,
                border: "none",
                padding: 0,
              }}
            ></TableCell>
            {weekDays.map((day) => (
              <TableCell
                key={day.toISOString()}
                sx={{
                  textAlign: "center",
                  borderRadius: "12px",
                  padding: "0px",
                  border: "none",
                  backgroundColor: isToday(day)
                    ? themeColors.background.highlight
                    : themeColors.background.paper,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isToday(day) ? 800 : 600,
                    color: themeColors.text.primary,
                  }}
                >
                  {format(day, "EEEE")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isToday(day) ? 600 : 400,
                    color: themeColors.text.primary,
                  }}
                >
                  {format(day, "MMM d")}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {timeSlots.map((time) => (
            <TableRow key={time.toISOString()} sx={{ height: "60px" }}>
              <TableCell
                sx={{
                  width: "80px",
                  height: "60px",
                  border: "none",
                  padding: 0,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: themeColors.text.secondary }}>
                    {format(time, "h:mm a")}
                  </Typography>
                </Box>
              </TableCell>
              {weekDays.map((day) => handleAppointmentShowed(day, time))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppointmentWeekView;
